import { I18nEditorProvider } from "@greco-it/i18n-editor";
import i18n, { glsPostProcessor } from "i18n";
import { ReactNode, useEffect, useState } from "react";
import { MSGraphAuth } from "service/GraphService";

/** The Glossa API, e.g. `https://api-d.grecoit.services/gls-be/v1`. Unset → no in-context editor. */
const glsApiUrl = (): string | undefined => process.env.REACT_APP_GLS_API_URL || undefined;
/** The scope of that instance's API; staging has its own (`api://greco-staging-gls/…`). */
const glsScope = (): string =>
  process.env.REACT_APP_GLS_API_SCOPE || "api://greco-gls/access_as_user";

/** The Entra app role of hbh's translators (it gated the Phrase in-context editor before). */
export const EDITOR_ROLE = "PHRASE";

/**
 * GLS-d, the Glossa instance hbh's dev build and `npm start` talk to. Against it the role gate is
 * off: every signed-in user gets the provider, and Glossa's own grant (`/access/me`) alone decides
 * who sees the toggle. Staging and production talk to their own instances and keep the role gate.
 * (`REACT_APP_ENVIRONMENT` cannot tell the builds apart: it is "D" in the committed .env and no
 * workflow overrides it.)
 */
export const DEV_GLS_API_URL = "https://api-d.grecoit.services/gls-be/v1";

const isOpenToAll = (apiBaseUrl: string): boolean =>
  apiBaseUrl.replace(/\/+$/, "") === DEV_GLS_API_URL;

const hasEditorRole = async (): Promise<boolean> => {
  const account = await MSGraphAuth.accountObj;
  const roles = (account?.idTokenClaims as { roles?: string[] } | undefined)?.roles;
  return !!roles?.includes(EDITOR_ROLE);
};

/**
 * Whether the page may open a sign-in popup now: only right after a click or key press. The
 * provider also asks for a token on page load (`/access/me`), and a popup there would greet every
 * user on dev — and is blocked by the browser anyway. Browsers without the API are not restricted.
 */
const userJustActed = (): boolean =>
  (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation?.isActive ??
  true;

/**
 * A token for the Glossa API from the app's own sign-in, silently first. If that fails and the user
 * has just acted (switched the editor on), a popup, not a redirect: a redirect would throw away the
 * page they wanted to fix. Without a user action, no token — the toggle then stays hidden for this
 * page load.
 */
export const getGlsToken = async (): Promise<string | null> => {
  const account = await MSGraphAuth.accountObj;
  if (!account) return null;
  const msal = MSGraphAuth.userAgentApplication;
  const request = { scopes: [glsScope()], account };
  try {
    return (await msal.acquireTokenSilent(request)).accessToken;
  } catch (error) {
    if (!userJustActed()) {
      console.warn("Glossa editor: no silent token; sign in again to use the editor.", error);
      return null;
    }
    return (await msal.acquireTokenPopup(request)).accessToken;
  }
};

/**
 * Wraps the app in the Glossa in-context editor (project `hbh`). On staging and production two
 * gates, both required: the app's own role (`PHRASE`) and Glossa's verdict (`GET /access/me`, asked
 * by the provider). On dev (GLS-d) only Glossa's verdict. Anybody else gets no provider at all, so
 * the Settings toggle, the Ctrl+Alt+T hotkey and `?gls_edit=1` have nothing to switch on. The
 * provider reads its access once, at mount, so it mounts only after the gate is settled.
 */
export const GlsEditor = ({ children }: { children: ReactNode }): JSX.Element => {
  const apiBaseUrl = glsApiUrl();
  const openToAll = !!apiBaseUrl && isOpenToAll(apiBaseUrl);
  const [hasRole, setHasRole] = useState(false);

  useEffect(() => {
    if (!apiBaseUrl || openToAll) return;
    let active = true;
    hasEditorRole()
      .then((allowed) => active && setHasRole(allowed))
      .catch((error: unknown) => console.error("Glossa editor: cannot read the account roles", error));
    return () => {
      active = false;
    };
  }, [apiBaseUrl, openToAll]);

  if (!apiBaseUrl || !(openToAll || hasRole)) return <>{children}</>;
  return (
    <I18nEditorProvider
      i18n={i18n}
      postProcessor={glsPostProcessor}
      project="hbh"
      apiBaseUrl={apiBaseUrl}
      getToken={getGlsToken}
    >
      {children}
    </I18nEditorProvider>
  );
};
